import { useEffect, useState } from 'react';
import {
	AuthError,
	checkSession,
	clearToken,
	getAdminGallery,
	getToken,
	login,
} from 'modules/web/admin';
import { setGallery as setPublicGallery } from 'modules/gallery';
import AlbumList from './AlbumList';
import AlbumEditor from './AlbumEditor';
import { Button, ErrorText, inputClass } from './ui';

const LoginForm = ({ onLogin }) => {
	const [code, setCode] = useState('');
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	const submit = async (e) => {
		e.preventDefault();
		setBusy(true);
		setError('');

		try {
			await login(code);
			onLogin();
		} catch (err) {
			setError(err.message);
		} finally {
			setBusy(false);
		}
	};

	return (
		<form
			onSubmit={submit}
			className='flex flex-col place-items-center justify-center gap-4 m-5 min-h-[50vh] font-standard'>
			<input
				className={`${inputClass} max-w-xs`}
				placeholder='Enter code'
				type='password'
				autoComplete='current-password'
				value={code}
				onChange={(e) => setCode(e.target.value)}
				autoFocus
			/>
			<Button type='submit' disabled={busy || !code}>
				{busy ? 'Checking…' : 'Log in'}
			</Button>
			<ErrorText>{error}</ErrorText>
		</form>
	);
};

export default () => {
	const [session, setSession] = useState(getToken() ? 'checking' : 'out');
	const [gallery, setGallery] = useState(null);
	const [selected, setSelected] = useState(null);
	const [error, setError] = useState('');

	const handleError = (err) => {
		if (err instanceof AuthError) {
			setSession('out');
			setGallery(null);
		}

		setError(err.message);
	};

	const refresh = async () => {
		try {
			const fresh = await getAdminGallery();

			setGallery(fresh);
			setPublicGallery(fresh);
		} catch (err) {
			handleError(err);
		}
	};

	useEffect(() => {
		if (session === 'checking') {
			checkSession()
				.then(() => setSession('in'))
				.catch((err) => {
					clearToken();
					setSession('out');
					if (!(err instanceof AuthError)) setError(err.message);
				});
		}

		if (session === 'in') refresh();
	}, [session]);

	if (session === 'checking') return null;

	if (session === 'out') {
		return (
			<>
				<LoginForm
					onLogin={() => {
						setError('');
						setSession('in');
					}}
				/>
				<div className='flex justify-center'>
					<ErrorText>{error}</ErrorText>
				</div>
			</>
		);
	}

	const albums = gallery?.albums || {};
	const album = selected && albums[selected];

	return (
		<div className='flex flex-col gap-5 p-5 font-standard max-w-6xl mx-auto w-full'>
			<div className='flex flex-row justify-between place-items-center gap-4'>
				<span className='text-2xl font-bld uppercase'>
					{album ? album.name : 'Photo albums'}
				</span>
				<div className='flex flex-row gap-2'>
					{album && (
						<Button
							variant='outline'
							onClick={() => setSelected(null)}>
							All albums
						</Button>
					)}
					<Button
						variant='outline'
						onClick={() => {
							clearToken();
							setSession('out');
							setGallery(null);
						}}>
						Log out
					</Button>
				</div>
			</div>
			<ErrorText onDismiss={() => setError('')}>{error}</ErrorText>
			{!gallery ? (
				<span>Loading…</span>
			) : album ? (
				<AlbumEditor
					key={selected}
					albumId={selected}
					album={album}
					onChanged={refresh}
					onDeleted={() => {
						setSelected(null);
						refresh();
					}}
					onError={handleError}
				/>
			) : (
				<AlbumList
					albums={albums}
					onOpen={setSelected}
					onCreated={async (albumId) => {
						await refresh();
						setSelected(albumId);
					}}
					onError={handleError}
				/>
			)}
		</div>
	);
};
